import React, { useState, useRef, useEffect } from 'react';
import { styled } from '@mui/system';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import Box from '@mui/material/Box';
import ArrowForwardIosRoundedIcon from '@mui/icons-material/ArrowForwardIosRounded';
import ArrowBackIosRoundedIcon from '@mui/icons-material/ArrowBackIosRounded';
import MediaQueryUtility from '@utils/MediaQueryUtility';
import ErrorImage from '@components/common/ErrorImage';
import { ErrorImageConvertUtility } from '@utils/ErrorImageConvertUtility';
import { MediaQueryUtilityClass } from '@/types/utils/MediaQueryUtilityClass';

function NextArrowIcon(props: any) {
  const { onClick } = props;
  return (
    <Box>
      <ArrowForwardIosRoundedIcon
        className="w-8 h-6 bg-gray-01 md:hover:opacity-60 rounded-2xl opacity-90 cursor-pointer"
        style={{
          display: 'block',
          position: 'absolute',
          top: '50%',
          right: '0',
          zIndex: '1',
        }}
        onClick={onClick}
        aria-label="次へ"
      />
    </Box>
  );
}

function PrevArrowIcon(props: any) {
  const { onClick } = props;
  return (
    <Box>
      <ArrowBackIosRoundedIcon
        className="w-8 h-6 bg-gray-01 md:hover:opacity-60 rounded-2xl opacity-90 cursor-pointer"
        style={{
          display: 'block',
          position: 'absolute',
          top: '50%',
          left: '0',
          zIndex: '1',
        }}
        onClick={onClick}
        aria-label="前へ"
      />
    </Box>
  );
}
function NextArrowIconThumbs(props: any) {
  const { onClick } = props;
  return (
    <Box>
      <ArrowForwardIosRoundedIcon
        className="w-6 h-4 bg-gray-01 md:hover:opacity-60 rounded-2xl opacity-90 cursor-pointer"
        style={{
          display: 'block',
          position: 'absolute',
          top: '50%',
          right: '0',
          zIndex: '1',
        }}
        onClick={onClick}
        aria-label="次へ"
      />
    </Box>
  );
}

function PrevArrowIconThumbs(props: any) {
  const { onClick } = props;
  return (
    <Box>
      <ArrowBackIosRoundedIcon
        className="w-6 h-4 bg-gray-01 md:hover:opacity-60 rounded-2xl opacity-90 cursor-pointer"
        style={{
          display: 'block',
          position: 'absolute',
          top: '50%',
          left: '0',
          zIndex: '1',
        }}
        onClick={onClick}
        aria-label="前へ"
      />
    </Box>
  );
}
type ImageProps = {
  id: string;
  image_url: {
    '2xl': string;
    lg: string;
    md: string;
    original: string;
    sm: string;
    xl: string;
    xs: string;
  };
  title: string;
  sort_id: number;
  image_branch_id: number;
  is_main: number;
  is_deleted: number;
  image_table_name: string;
  created_at: string;
  updated_at: string;
};

type ImagePathProps = {
  mansionName: string;
  images: ImageProps[];
};

const StyledSlider = styled(Slider)({
  '& .slick-dots': {
    bottom: '-72px !important',
    '& li': {
      width: 'auto !important',
      height: 'auto !important',
    },
    '@media print, screen and (max-width: 767px)': {
      bottom: '-46px !important',
      textAlign: 'left !important',
      paddingLeft: '20px !important',
    },
    '@media print, screen and (max-width: 320px)': {
      paddingLeft: '0 !important',
    },
  },
  '& .slick-prev': {
    left: '0 !important',
  },
  '& .slick-next': {
    right: '0 !important',
  },
  '& .slick-prev, & .slick-next': {
    zIndex: 1,
  },
  '& .slick-list': {
    '@media print, screen and (max-width: 767px)': {
      maxHeight: '237px',
    },
  },
  '& .sliderImg': {
    '@media print, screen and (max-width: 767px)': {
      maxWidth: '300px',
    },
  },
});

const StyledSliderThumbs = styled(Slider)<{ imageCount: number }>(({ imageCount }) => ({
  '& .slick-slide': {
    width: 'auto !important',
  },
  '& .slick-dots': {
    bottom: '-72px !important',
    '& li': {
      width: 'auto !important',
      height: 'auto !important',
    },
    '@media print, screen and (max-width: 767px)': {
      bottom: '-46px !important',
      textAlign: 'left !important',
      paddingLeft: '20px !important',
    },
    '@media print, screen and (max-width: 320px)': {
      paddingLeft: '0 !important',
    },
  },
  '& .slick-prev': {
    left: '0 !important',
  },
  '& .slick-next': {
    right: '0 !important',
  },
  '& .slick-prev, & .slick-next': {
    zIndex: 1,
  },
  '& .slick-list': {
    '@media print, screen and (max-width: 767px)': {
      maxHeight: '237px',
    },
  },
  '& .slick-track': {
    gap: '8px',
    display: 'flex',
    cursor: 'pointer',
    '@media print, screen and (min-width: 768px)': {
      // スライダーではない時のみ適用
      ...(imageCount <= 12 && {
        maxWidth: '80rem !important',
        width: '100% !important',
      }),
      justifyContent: 'center',
      marginLeft: '0',
      marginRight: '0',
    },
    '@media print, screen and (max-width: 767px)': {
      margin: '12px 0 0 10px',
    },
  },
  '& .sliderImg': {
    '@media print, screen and (max-width: 767px)': {},
  },
}));

export default function ImageSlider(props: ImagePathProps) {
  const { mansionName, images } = props;
  const imageArr: ImageProps[] = [];
  for (let i = 0; i < images.length; i += 1) {
    imageArr.push(images[i]);
  }
  const slidesToShow = imageArr.length > 12 ? 8 : imageArr.length;
  const slidesToShowSP = imageArr.length > 5 ? 5 : imageArr.length;
  const [nav1, setNav1] = useState<Slider | undefined>(undefined);
  const [nav2, setNav2] = useState<Slider | undefined>(undefined);
  const sliderRef1 = useRef<Slider | null>(null);
  const sliderRef2 = useRef<Slider | null>(null);
  const mediaQueryUtility: MediaQueryUtilityClass = MediaQueryUtility;

  useEffect(() => {
    setNav1(sliderRef1.current || undefined);
    setNav2(sliderRef2.current || undefined);
  }, []);

  return (
    <section className="mt-5 max-md:mt-4 mb-12 max-md:mb-4">
      <StyledSlider
        asNavFor={nav2}
        ref={sliderRef1}
        dots={false}
        dotsClass="slick-dots slick-thumb"
        infinite
        speed={500}
        slidesToScroll={1}
        centerMode
        centerPadding="10px"
        slidesToShow={1}
        variableWidth
        arrows
        nextArrow={<NextArrowIcon />}
        prevArrow={<PrevArrowIcon />}
      >
        {imageArr.map((image: ImageProps) => {
          let altText = mansionName;
          if (image.title) {
            altText = image.title;
          }

          return (
            <div key={image.id}>
              <picture>
                <source media={mediaQueryUtility.getMediaQuery('sp', '@3x')} srcSet={image.image_url['2xl']} />
                <source media={mediaQueryUtility.getMediaQuery('sp', '@2x')} srcSet={image.image_url.xl} />
                <source media={mediaQueryUtility.getMediaQuery('sp')} srcSet={image.image_url.md} />
                <source media={mediaQueryUtility.getMediaQuery('pc', '@3x')} srcSet={image.image_url['2xl']} />
                <source media={mediaQueryUtility.getMediaQuery('pc', '@2x')} srcSet={image.image_url['2xl']} />
                <source media={mediaQueryUtility.getMediaQuery('pc')} srcSet={image.image_url.lg} />
                <ErrorImage
                  alt={altText}
                  className="sliderImg w-full h-96 max-md:h-[237px] px-1 object-cover"
                  width="51"
                  height="38"
                  onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                    ErrorImageConvertUtility.convertErrorImage(event.currentTarget);
                  }}
                />
              </picture>
            </div>
          );
        })}
      </StyledSlider>
      <StyledSliderThumbs
        className="md:max-w-7xl md:w-full md:mx-auto md:px-24"
        asNavFor={nav1}
        ref={sliderRef2}
        slidesToShow={slidesToShow}
        infinite
        centerMode
        arrows
        nextArrow={<NextArrowIconThumbs />}
        prevArrow={<PrevArrowIconThumbs />}
        focusOnSelect
        imageCount={imageArr.length}
        responsive={[
          {
            breakpoint: 767,
            settings: {
              slidesToShow: slidesToShowSP,
            },
          },
        ]}
      >
        {imageArr.map((image: ImageProps, index: number) => (
          <div key={image.id}>
            <picture>
              <source srcSet={image.image_url.xs} />
              <ErrorImage
                alt={`${mansionName}のサムネイル${index + 1}`}
                className="w-[86px] max-md:w-[50px] h-14 max-md:h-[37.5px] aspect-video object-cover"
                width="86"
                height="50"
                onError={(event: React.SyntheticEvent<HTMLImageElement>) => {
                  ErrorImageConvertUtility.convertErrorImage(event.currentTarget);
                }}
              />
            </picture>
          </div>
        ))}
      </StyledSliderThumbs>
    </section>
  );
}
